import {
  type Form,
  type FieldsInputField,
  InputFieldInputType,
  type _Number,
  InputFieldNumberComponentType,
  type InputFieldPayment,
  InputFieldPaymentComponentType,
  type _String,
  InputFieldStringComponentType,
} from '@wix/ambassador-forms-v4-form/types';
import { type FormValidator } from '../submission-validation/form-validator';
import { type Submission } from '../../submission';
import { type ValidationResult } from '../submission-validation/errors';

export class FormSchemaValidator {
  constructor(private readonly submissionValidator: FormValidator) {}

  validate(form: Form): ValidationResult {
    const submission =
      form.formFields?.reduce((acc, { inputOptions }) => {
        const defaultValue = this.getDefaultValue(inputOptions);
        const target = inputOptions?.target;
        if (defaultValue && target) {
          acc[target] = this.buildSubmissionValue(defaultValue, inputOptions);
        }
        return acc;
      }, {} as Submission) || {};

    if (Object.keys(submission).length) {
      return this.submissionValidator.validate(form.id!, submission);
    }
    return { errors: [] };
  }

  private getDefaultValue(
    field?: FieldsInputField,
  ): string | number | undefined {
    switch (field?.inputType) {
      case InputFieldInputType.STRING:
        return (
          this.getDefaultStringComponentValue(field.stringOptions) ?? undefined
        );
      case InputFieldInputType.NUMBER:
        return (
          this.getDefaultNumberComponentValue(field.numberOptions) ?? undefined
        );
      case InputFieldInputType.PAYMENT:
        return (
          this.getDefaultPaymentComponentValue(field.paymentOptions) ??
          undefined
        );
      default:
        return undefined;
    }
  }

  private getDefaultStringComponentValue(
    stringOptions?: _String,
  ): string | null | undefined {
    switch (stringOptions?.componentType) {
      case InputFieldStringComponentType.DATE_TIME:
        return stringOptions?.dateTimeOptions?.default;
      case InputFieldStringComponentType.DATE_INPUT:
        return stringOptions?.dateInputOptions?.default;
      case InputFieldStringComponentType.DATE_PICKER:
        return stringOptions?.datePickerOptions?.default;
      case InputFieldStringComponentType.TEXT_INPUT:
        return stringOptions?.textInputOptions?.default;
      case InputFieldStringComponentType.TIME_INPUT:
        return stringOptions?.timeInputOptions?.default;
      default:
        return undefined;
    }
  }

  private getDefaultNumberComponentValue(
    numberOptions?: _Number,
  ): number | null | undefined {
    switch (numberOptions?.componentType) {
      case InputFieldNumberComponentType.NUMBER_INPUT:
        return numberOptions?.numberInputOptions?.default;
      case InputFieldNumberComponentType.RATING_INPUT:
        return numberOptions?.ratingInputOptions?.defaultValue;
      default:
        return undefined;
    }
  }

  private getDefaultPaymentComponentValue(
    paymentOptions?: InputFieldPayment,
  ): number | null | undefined {
    switch (paymentOptions?.componentType) {
      case InputFieldPaymentComponentType.PAYMENT_INPUT:
        return paymentOptions?.paymentInputOptions?.default;
      default:
        return undefined;
    }
  }

  private buildSubmissionValue(
    defaultValue: string | number,
    inputField: FieldsInputField,
  ) {
    switch (inputField.inputType) {
      case InputFieldInputType.PAYMENT:
        return inputField.paymentOptions?.validation?.products?.map(
          (product) => ({
            productId: product.id,
            price: defaultValue,
            quantity: 1,
          }),
        );
      default:
        return defaultValue;
    }
  }
}
